"use client";

import type { SupDataGridColumn, SupWeb3VaultBalance } from "@/types";
import React from "react";

import config from "@/../config/dapp.config";

import GridCellAccountPositionRewards from "@/components/Grid/Cell/Account/PositionRewards";
import GridCellPositionName from "@/components/Grid/Cell/PositionName";
import GridCellTransmuteMenu from "@/components/Grid/Cell/TransmuteMenu";
import GridHeaderCellApy from "@/components/Grid/HeaderCell/Apy";
import useRewardSettings from "@/hooks/data/useRewardSettings";
import GridCellCropped from "@/components/Grid/Cell/Cropped";
import GridCellAvailability, { AVAILABILITY_DICT } from "@/components/Grid/Cell/Availability";
import GridCellYieldType from "@/components/Grid/Cell/YieldType";
import GridCellDate from "@/components/Grid/Cell/Date";
import GridCellProtocol from "@/components/Grid/Cell/Protocol";
import GridCellToggleFavorite from "@/components/Grid/Cell/ToggleFavorite";
import GridCellRewardApy from "@/components/Grid/Cell/RewardApy";
import UiLabelSharpe from "@/components/Ui/Label/Sharpe";
import UiLabelTvl from "@/components/Ui/Label/Tvl";
import { type Column, SupDataGrid } from "@repo/core-ui";
import { useFilterGridColumns } from "@/store";
import CartButton from "@/components/Adapter/CartButton";

import { getProtocolName, getSortingDate, formatCurrencyCompact, getVaultTvl, getVaultSharpe, getNetVaultApy, UiLabelChain } from "@/utils";

interface PortfolioTableProps {
  superPositions: SupWeb3VaultBalance[] | undefined;
  isLoading?: boolean;
  isFiltered?: boolean;
  hideColumns?: string[];
  controlledColumns?: SupDataGridColumn[];
  portfolioValue: number;
}

export default function PortfolioTable({ superPositions, isFiltered, isLoading, controlledColumns, hideColumns = [], portfolioValue }: PortfolioTableProps) {
  const totalPortfolioUsd = portfolioValue || 0;

  const rewardSettings = useRewardSettings();

  const columns: Column<SupWeb3VaultBalance>[] = [
    {
      width: 20,
      field: "favorite",
      resizable: false,
      sortable: false,
      headerName: "",
      align: "center",
      renderCell: row => <GridCellToggleFavorite singleVault={row.vault} />,
    },
    {
      field: "percentage",
      type: "number",
      align: "right",
      width: 90,
      headerName: "%",
      valueGetter: row => (totalPortfolioUsd > 0 ? (parseFloat(`${row.superposition_usd_value}`) / totalPortfolioUsd) * 100 : 0),
      renderCell: row => {
        const percentage = totalPortfolioUsd > 0 ? (parseFloat(`${row.superposition_usd_value}`) / totalPortfolioUsd) * 100 : 0;
        return `${percentage.toFixed(2)}%`;
      },
    },
    {
      field: "friendly_name",
      headerName: "Position",
      minWidth: 320,
      draggable: false,
      type: "string",
      valueGetter: row => row.vault.friendly_name,
      renderCell: row => <GridCellPositionName singlePosition={row} />,
    },
    {
      field: "superposition_usd_value",
      type: "number",
      headerName: "Value",
      align: "left",
      width: 160,
      valueGetter: row => parseFloat(`${row.superposition_usd_value}`),
      renderCell: row => formatCurrencyCompact(parseFloat(`${row.superposition_usd_value}`), config.decimalsRoundingLimit, 0, "currency", "standard"),
    },
    {
      field: "protocol",
      headerName: "Protocol",
      width: 170,
      valueGetter: row => getProtocolName(row.vault),
      renderCell: row => <GridCellProtocol vault={row.vault} />,
    },
    {
      field: "apy_day",
      headerName: "24H APY",
      align: "right",
      type: "number",
      width: 120,
      resizable: false,
      valueGetter: row => getNetVaultApy(row.vault, rewardSettings, "day"),
      renderCell: row => <GridCellRewardApy vault={row.vault} period="day" />,
    },
    {
      field: "apy_week",
      headerName: "7D APY",
      align: "right",
      width: 120,
      type: "number",
      resizable: false,
      valueGetter: row => getNetVaultApy(row.vault, rewardSettings, "week"),
      renderCell: row => <GridCellRewardApy vault={row.vault} period="week" />,
    },
    {
      field: "apy",
      width: 120,
      headerName: <GridHeaderCellApy />,
      align: "right",
      type: "number",
      resizable: false,
      valueGetter: row => getNetVaultApy(row.vault, rewardSettings),
      renderCell: row => <GridCellRewardApy vault={row.vault} />,
    },
    {
      headerName: "Minted On",
      field: "chain_id",
      draggable: false,
      resizable: false,
      valueGetter: row => row.chain_id,
      renderCell: row => UiLabelChain(row.chain_id as number, true, true, false, "border-none img-bigger w-3 h-3", 12),
    },

    {
      field: "tvl",
      headerName: "TVL",
      align: "right",
      type: "number",
      width: 120,
      valueGetter: row => getVaultTvl(row.vault),
      renderCell: row => <UiLabelTvl vault={row.vault} />,
    },
    {
      field: "rewards",
      headerName: "Rewards",
      align: "left",
      sortable: false,
      minWidth: 160,
      valueGetter: row => row.vault.rewards?.reduce((acc, reward) => acc + reward.address, ""),
      renderCell: row => <GridCellAccountPositionRewards singlePosition={row} />,
    },

    {
      field: "yield_type",
      headerName: "Yield Type",
      renderCell: row => <GridCellYieldType vault={row.vault} />,
      valueGetter: row => row.vault.yield_type,
    },
    {
      field: "sharpe",
      width: 120,
      headerName: "Sharpe",
      type: "number",
      align: "right",
      valueGetter: row => getVaultSharpe(row.vault),
      renderCell: row => <UiLabelSharpe vault={row.vault} />,
    },
    {
      headerName: "ID",
      field: "id",
      draggable: false,
      resizable: false,
      valueGetter: row => row.vault.id,
      renderCell: row => <GridCellCropped value={row.vault.id} front={3} back={3} />,
    },
    {
      field: "availability",
      headerName: "Availability",
      type: "number",
      width: 140,
      align: "right",
      renderCell: row => <GridCellAvailability vault={row.vault} />,
      valueGetter: row => AVAILABILITY_DICT[row.vault.availability],
    },
    {
      field: "created_at",
      headerName: "List date",
      width: 140,
      align: "right",
      type: "number",
      valueGetter: row => getSortingDate(row.vault.created_at),
      renderCell: row => <GridCellDate object={row.vault} />,
    },
    {
      field: "actions",
      headerName: "",
      resizable: false,
      draggable: false,
      sortable: false,
      align: "left",
      width: 210,
      renderCell: row => (
        <div className="flex items-center space-x-2">
          <CartButton vaultBalance={row} singleVault={row.vault} />
          <GridCellTransmuteMenu singlePosition={row} />
        </div>
      ),
    },
  ];

  const { defaultSorting, excludeColumns, setSorting } = useFilterGridColumns(
    "Portfolio",
    "superposition_usd_value",
    "superposition_usd_value",
    "DESC",
    "DESC",
    hideColumns.concat(["vault_id"]),
    controlledColumns
  );

  return (
    <div className="sup-grid-container-page w-full">
      <SupDataGrid
        enableVirtualization
        className="w-full min-w-full"
        filtered={isFiltered}
        loading={isLoading}
        rowHeight={88}
        columns={columns}
        excludeColumns={excludeColumns}
        data={superPositions || []}
        defaultSorting={defaultSorting}
        onSortChange={sorting => setSorting(sorting[0])}
        rowId={row => `${row.chain_id}_${row.vault.id}_${row.is_erc20 ? "ERC20" : "1155A"}`}
      />
    </div>
  );
}
